import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { GCOMPRIS_CATEGORIES, gcomprisCategoryIconUrl } from '../../../utils/gcompris';

function ScrollButton({ direction, onClick }) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Scroll categories left' : 'Scroll categories right'}
      className="h-10 w-10 shrink-0 rounded-full border border-gray-200 bg-white text-indigo-500 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
    >
      <Icon size={20} />
    </button>
  );
}

/**
 * The row of GCompris category pictures that filters the activity grid. The
 * arrows only appear when the row is wider than the space it has (narrow
 * windows, or a sidebar that has just been expanded).
 */
export default function GComprisCategoryStrip({ category, onSelect }) {
  const scrollerRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = scrollerRef.current;
    if (typeof ResizeObserver === 'undefined' || !el) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const scrollBy = (direction) =>
    scrollerRef.current?.scrollBy({ left: direction * 240, behavior: 'smooth' });

  return (
    <div className="flex items-center gap-2 mb-5">
      {canScroll.left && <ScrollButton direction="left" onClick={() => scrollBy(-1)} />}

      <div
        ref={scrollerRef}
        onScroll={measure}
        role="group"
        aria-label="Activity categories"
        className="flex-1 min-w-0 flex gap-3 overflow-x-auto pt-1 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {GCOMPRIS_CATEGORIES.map(({ id, label }) => {
          const selected = id === category;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(id)}
              aria-pressed={selected}
              className={`relative shrink-0 w-[104px] flex flex-col items-center gap-1 rounded-2xl border-2 px-2 pt-2 pb-2 shadow-sm transition-colors ${
                selected
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
              }`}
            >
              <img src={gcomprisCategoryIconUrl(id)} alt="" className="h-14 w-14 object-contain" />
              <span className={`text-[14px] font-bold ${selected ? 'text-orange-700' : 'text-slate-700'}`}>{label}</span>
              {selected && (
                <span
                  aria-hidden="true"
                  className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 border-x-[9px] border-t-[9px] border-x-transparent border-t-orange-400"
                />
              )}
            </button>
          );
        })}
      </div>

      {canScroll.right && <ScrollButton direction="right" onClick={() => scrollBy(1)} />}
    </div>
  );
}
